import { Component, computed, effect, input, output } from "@angular/core";
import { UiKitModule } from "@app/ui-kit.module";
import { ProductCardWithQuantityComponent } from "@components/product-card-with-quantity/product-card-with-quantity.component";
import { ProductCardComponent } from "@components/product-card/product-card.component";
import { Product, ProductPropertyDimension, ProductPropertyText } from "@domain/product/product";
import { ProductAsset } from "@domain/product/product-asset";
import { ProductImage } from "@domain/product/product-image";
import { GasWarningCenterType } from "@project/gas-warning-center-configuration/gas-warning-center-type";

@Component({
  selector: "app-gas-warning-center-card",
  templateUrl: "./gas-warning-center-card.component.html",
  styleUrls: ["./gas-warning-center-card.component.scss"],
  imports: [UiKitModule, ProductCardComponent, ProductCardWithQuantityComponent],
  standalone: true,
})
export class GasWarningCenterCardComponent {
  readonly gasWarningCenterData = input.required<Product>();
  readonly selectionMode = input.required<"single" | "multi">();
  readonly showCosts = input.required<boolean>();
  readonly quantity = input<number>(0);
  readonly preselectedQuantity = input<number | null>(null);
  readonly type = input.required<GasWarningCenterType>();

  readonly quantityChanged$ = output<number>();

  protected readonly fallbackImageUrl = computed(() => {
    const cantoImageId = this.type().cantoImageId;
    return cantoImageId ? ProductImage.getCantoSourceUrl(cantoImageId) : undefined;
  });
  protected readonly imageUrl = computed(() => {
    const cantoImageId = this.gasWarningCenterData().cantoImageId;
    return cantoImageId ? ProductImage.getCantoSourceUrl(cantoImageId) : this.fallbackImageUrl();
  });
  protected readonly productInfoUrl = computed(() => {
    const cantoId = this.gasWarningCenterData().cantoProductInfoId;
    return cantoId ? ProductAsset.getCantoSourceUrl(cantoId) : undefined;
  });

  dimensionsProperty?: ProductPropertyDimension;
  powersupplyProperty?: ProductPropertyText;
  degreeprotectionProperty?: ProductPropertyText;

  private readonly POWERSUPPLY = "Power_Supply_";
  private readonly DIMENSIONS = "Dimensions_";
  private readonly DEGREEPROTECTION = "Degree_protection_";

  constructor() {
    effect(() => {
      this.mapProperties(this.gasWarningCenterData());
    });

    effect(() => {
      // trigger form selection once if preselected value
      const quantity = this.preselectedQuantity();
      if (quantity) {
        this.quantityChanged$.emit(quantity);
      }
    });
  }

  private mapProperties(gasWarningCenterData: Product) {
    this.powersupplyProperty = gasWarningCenterData.getTextPropertyByKey(this.POWERSUPPLY);
    this.degreeprotectionProperty = gasWarningCenterData.getTextPropertyByKey(this.DEGREEPROTECTION);
    this.dimensionsProperty = gasWarningCenterData.getDimensionsPropertyByKey(this.DIMENSIONS);
  }
}
